import React, { Component } from 'react'
import { MachineData, Component } from '../types/types'
import { formatNumber, formatDecimal } from '../utils/formatters'
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  BarChart3,
  Calendar,
  Droplets,
  Wind,
  Package,
  Zap,
  Thermometer,
  Brush,
  Wrench,
  Cylinder,
  Stamp,
  ArrowRight,
} from 'lucide-react'
interface MachineDetailsProps {
  machine: MachineData
}
const ComponentStatus: React.FC<{
  status: string
}> = ({ status }) => {
  switch (status) {
    case 'optimal':
      return <CheckCircle2 className="text-green-400" size={18} />
    case 'warning':
      return <AlertCircle className="text-yellow-400" size={18} />
    case 'critical':
      return <XCircle className="text-red-400" size={18} />
    default:
      return <CheckCircle2 className="text-gray-400" size={18} />
  }
}
// Funktion zur Bestimmung der Rohstoffverbrauchsdaten basierend auf dem Maschinentyp
const getResourceConsumption = (machineType: string, efficiency: number) => {
  // Effizienzfaktor: Je höher die Effizienz, desto niedriger der Verbrauch
  const efficiencyFactor = (100 - efficiency) / 100 + 0.5
  switch (machineType) {
    case 'ausbeulanlage':
      return [
        {
          name: 'Hydrauliköl',
          amount: (0.2 * efficiencyFactor).toFixed(2),
          unit: 'L/Fass',
          icon: <Droplets size={20} className="text-blue-400" />,
        },
        {
          name: 'Strom',
          amount: (1.5 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
    case 'waschanlage':
      return [
        {
          name: 'Wasser',
          amount: (12 * efficiencyFactor).toFixed(1),
          unit: 'L/Fass',
          icon: <Droplets size={20} className="text-blue-400" />,
        },
        {
          name: 'Reinigungsmittel',
          amount: (0.3 * efficiencyFactor).toFixed(2),
          unit: 'L/Fass',
          icon: <Package size={20} className="text-green-400" />,
        },
        {
          name: 'Strom',
          amount: (0.8 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
    case 'lackieranlage':
      return [
        {
          name: 'Farbe',
          amount: (0.5 * efficiencyFactor).toFixed(2),
          unit: 'L/Fass',
          icon: <Brush size={20} className="text-purple-400" />,
        },
        {
          name: 'Strom',
          amount: (1.2 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
        {
          name: 'Druckluft',
          amount: (5 * efficiencyFactor).toFixed(1),
          unit: 'm³/Fass',
          icon: <Wind size={20} className="text-blue-300" />,
        },
      ]
    case 'roboterarm':
      return [
        {
          name: 'Strom',
          amount: (2.5 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
        {
          name: 'Schmiermittel',
          amount: (0.05 * efficiencyFactor).toFixed(3),
          unit: 'L/Fass',
          icon: <Droplets size={20} className="text-gray-400" />,
        },
      ]
    case 'faesserBuerste':
      return [
        {
          name: 'Bürstenabnutzung',
          amount: (0.02 * efficiencyFactor).toFixed(3),
          unit: 'Einheiten/Fass',
          icon: <Brush size={20} className="text-purple-400" />,
        },
        {
          name: 'Strom',
          amount: (0.6 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
        {
          name: 'Wasser',
          amount: (2 * efficiencyFactor).toFixed(1),
          unit: 'L/Fass',
          icon: <Droplets size={20} className="text-blue-400" />,
        },
      ]
    case 'sandstrahlmaschine':
      return [
        {
          name: 'Sand',
          amount: (3.5 * efficiencyFactor).toFixed(2),
          unit: 'kg/Fass',
          icon: <Wind size={20} className="text-amber-400" />,
        },
        {
          name: 'Druckluft',
          amount: (15 * efficiencyFactor).toFixed(1),
          unit: 'm³/Fass',
          icon: <Wind size={20} className="text-blue-300" />,
        },
        {
          name: 'Strom',
          amount: (1.2 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
    case 'buersteMaschine':
      return [
        {
          name: 'Bürstenabnutzung',
          amount: (0.03 * efficiencyFactor).toFixed(3),
          unit: 'Einheiten/Fass',
          icon: <Brush size={20} className="text-purple-400" />,
        },
        {
          name: 'Strom',
          amount: (0.7 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
    case 'dichtpruefungsmaschine':
      return [
        {
          name: 'Druckluft',
          amount: (8 * efficiencyFactor).toFixed(1),
          unit: 'm³/Fass',
          icon: <Wind size={20} className="text-blue-300" />,
        },
        {
          name: 'Dichtmittel',
          amount: (0.1 * efficiencyFactor).toFixed(2),
          unit: 'L/Fass',
          icon: <Cylinder size={20} className="text-gray-400" />,
        },
        {
          name: 'Strom',
          amount: (0.5 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
    case 'trocknung':
      return [
        {
          name: 'Erdgas',
          amount: (0.4 * efficiencyFactor).toFixed(2),
          unit: 'm³/Fass',
          icon: <Thermometer size={20} className="text-red-400" />,
        },
        {
          name: 'Strom',
          amount: (1.8 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
    case 'signiermaschine':
      return [
        {
          name: 'Tinte',
          amount: (0.05 * efficiencyFactor).toFixed(3),
          unit: 'L/Fass',
          icon: <Stamp size={20} className="text-indigo-400" />,
        },
        {
          name: 'Strom',
          amount: (0.3 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
    default:
      return [
        {
          name: 'Strom',
          amount: (1.0 * efficiencyFactor).toFixed(2),
          unit: 'kWh/Fass',
          icon: <Zap size={20} className="text-yellow-400" />,
        },
      ]
  }
}
export const MachineDetails: React.FC<MachineDetailsProps> = ({ machine }) => {
  // Berechnung der Verarbeitungsraten basierend auf Effizienz und anderen Metriken
  const baseRate =
    Math.round(
      (machine.metrics.efficiency / 10) *
        (machine.metrics.currentLoad / 100) *
        10,
    ) / 10
  const ratePerMinute = baseRate
  const ratePerHour = Math.round(ratePerMinute * 60)
  const ratePerDay = Math.round(ratePerHour * 24)
  // Rohstoffverbrauch basierend auf Maschinentyp
  const resourceConsumption = getResourceConsumption(
    machine.type,
    machine.metrics.efficiency,
  )
  // Berechne den Gesamtverbrauch pro Stunde/Tag für jede Ressource
  const calculateTotalConsumption = (
    amount: string,
    unit: string,
    rate: number,
  ) => {
    const numAmount = parseFloat(amount)
    const totalAmount = numAmount * rate
    return {
      hourly: formatDecimal(totalAmount, 2),
      daily: formatDecimal(totalAmount * 24, 1),
      unit: unit.replace('/Fass', ''),
    }
  }
  return (
    <div className="space-y-6">
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60">
        <h3 className="text-xl font-medium mb-4 text-gray-200">
          Komponenten-Status
        </h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b border-gray-700">
                <th className="pb-3 pr-6 text-gray-400">Komponente</th>
                <th className="pb-3 pr-6 text-gray-400">Status</th>
                <th className="pb-3 pr-6 text-gray-400">Letzte Wartung</th>
                <th className="pb-3 pr-6 text-gray-400">Betriebsstunden</th>
                <th className="pb-3 text-gray-400">Leistung</th>
              </tr>
            </thead>
            <tbody>
              {machine.components.map((component: Component) => (
                <tr key={component.id} className="border-b border-gray-700/50">
                  <td className="py-3 pr-6 font-medium">{component.name}</td>
                  <td className="py-3 pr-6">
                    <div className="flex items-center gap-2">
                      <ComponentStatus status={component.status} />
                      <span
                        className={`
                        ${component.status === 'optimal' ? 'text-green-400' : ''}
                        ${component.status === 'warning' ? 'text-yellow-400' : ''}
                        ${component.status === 'critical' ? 'text-red-400' : ''}
                      `}
                      >
                        {component.status === 'optimal' ? 'Optimal' : ''}
                        {component.status === 'warning' ? 'Warnung' : ''}
                        {component.status === 'critical' ? 'Kritisch' : ''}
                      </span>
                    </div>
                  </td>
                  <td className="py-3 pr-6 text-gray-300">
                    {component.lastMaintenance}
                  </td>
                  <td className="py-3 pr-6 text-gray-300">
                    {formatNumber(component.operatingHours)}h
                  </td>
                  <td className="py-3">
                    <div className="w-full bg-gray-700 rounded-full h-2.5">
                      <div
                        className={`h-2.5 rounded-full ${component.performance >= 80 ? 'bg-green-500' : component.performance >= 50 ? 'bg-yellow-500' : 'bg-red-500'}`}
                        style={{
                          width: `${component.performance}%`,
                        }}
                      ></div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60">
        <h3 className="text-xl font-medium mb-4 text-gray-200">
          Verarbeitungseffizienz
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gray-700/40 p-5 rounded-xl border border-gray-600/50 flex flex-col items-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-blue-500/20 mb-3">
              <Clock className="text-blue-400" size={24} />
            </div>
            <p className="text-gray-300 text-sm mb-1">Pro Minute</p>
            <p className="text-3xl font-bold text-white">
              {formatDecimal(ratePerMinute, 1)}
            </p>
            <p className="text-gray-400 text-sm">Fässer/Minute</p>
          </div>
          <div className="bg-gray-700/40 p-5 rounded-xl border border-gray-600/50 flex flex-col items-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-500/20 mb-3">
              <BarChart3 className="text-green-400" size={24} />
            </div>
            <p className="text-gray-300 text-sm mb-1">Pro Stunde</p>
            <p className="text-3xl font-bold text-white">
              {formatNumber(ratePerHour)}
            </p>
            <p className="text-gray-400 text-sm">Fässer/Stunde</p>
          </div>
          <div className="bg-gray-700/40 p-5 rounded-xl border border-gray-600/50 flex flex-col items-center">
            <div className="flex items-center justify-center w-12 h-12 rounded-full bg-purple-500/20 mb-3">
              <Calendar className="text-purple-400" size={24} />
            </div>
            <p className="text-gray-300 text-sm mb-1">Pro Tag</p>
            <p className="text-3xl font-bold text-white">
              {formatNumber(ratePerDay)}
            </p>
            <p className="text-gray-400 text-sm">Fässer/Tag</p>
          </div>
        </div>
        <div className="mt-6 p-4 bg-gray-700/30 rounded-lg border border-gray-600/30">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-blue-400"></div>
            <span className="text-gray-200 font-medium">
              Aktuelle Auslastung: {machine.metrics.currentLoad}%
            </span>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-3 h-3 rounded-full bg-green-400"></div>
            <span className="text-gray-200 font-medium">
              Effizienz: {machine.metrics.efficiency}%
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
            <span className="text-gray-200 font-medium">
              Durchschnittliche Effizienz: {machine.analytics.averageEfficiency}
              %
            </span>
          </div>
        </div>
      </div>
      {/* Aktualisierte Rohstoffverbrauch-Sektion */}
      <div className="bg-gray-800 rounded-xl p-6 border border-gray-700 shadow-lg backdrop-blur-sm bg-opacity-60">
        <h3 className="text-xl font-medium mb-4 text-gray-200">
          Rohstoffverbrauch
        </h3>
        {/* Ressourcen-Karten mit Pro-Fass und Gesamtverbrauch */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resourceConsumption.map((resource, index) => {
            const totalConsumption = calculateTotalConsumption(
              resource.amount,
              resource.unit,
              ratePerHour,
            )
            return (
              <div
                key={index}
                className="bg-gray-700/40 p-5 rounded-xl border border-gray-600/50"
              >
                <div className="flex items-center gap-3 mb-3">
                  {resource.icon}
                  <h4 className="text-lg font-medium text-gray-200">
                    {resource.name}
                  </h4>
                </div>
                {/* Verbrauch pro Fass */}
                <div className="flex justify-between items-baseline mb-2">
                  <p className="text-2xl font-bold text-white">
                    {resource.amount}
                  </p>
                  <p className="text-gray-400 text-sm">{resource.unit}</p>
                </div>
                {/* Verbrauchsbalken */}
                <div className="mb-4">
                  <div className="w-full bg-gray-600/50 h-1.5 rounded-full">
                    <div
                      className="h-1.5 rounded-full bg-blue-500"
                      style={{
                        width: `${Math.min(parseFloat(resource.amount) * 10, 100)}%`,
                      }}
                    ></div>
                  </div>
                </div>
                {/* Gesamtverbrauch */}
                <div className="bg-gray-800/50 p-3 rounded-lg border border-gray-600/30">
                  <div className="text-sm text-gray-300 mb-2 flex items-center gap-1">
                    <ArrowRight size={14} className="text-blue-400" />
                    <span>Aktueller Gesamtverbrauch:</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="text-white font-medium">
                        {totalConsumption.hourly} {totalConsumption.unit}/h
                      </div>
                      <div className="text-xs text-gray-400">pro Stunde</div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-medium">
                        {totalConsumption.daily} {totalConsumption.unit}/Tag
                      </div>
                      <div className="text-xs text-gray-400">pro Tag</div>
                    </div>
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        <div className="mt-6 p-4 bg-gray-700/30 rounded-lg border border-gray-600/30">
          <p className="text-gray-300 text-sm">
            <span className="font-medium text-gray-200">Hinweis:</span> Die
            Verbrauchswerte basieren auf der aktuellen Effizienz von{' '}
            {machine.metrics.efficiency}% und Auslastung von{' '}
            {machine.metrics.currentLoad}%. Eine höhere Effizienz führt zu einem
            geringeren Rohstoffverbrauch pro verarbeitetem Fass und damit auch
            zu niedrigerem Gesamtverbrauch.
          </p>
        </div>
      </div>
    </div>
  )
}
